import React, { useState, useEffect, useRef, useContext } from "react";
import {
  Box, Button, Select, TextField, FormControl, InputLabel, MenuItem, FormHelperText, FormControlLabel, Checkbox,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Header from "../../components/Header";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../../config";
import { AuthContext } from "../../components/AuthContext";
import {
  isFirebaseStorageEnabled,
  uploadProfileImageToFirebase,
} from "../../utils/profileImageStorage";

const ManageMember = () => {
  const [enrollments, setEnrollments] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [countries, setCountries] = useState([]);
  const [branches, setBranches] = useState([]);
  const [genders, setGenders] = useState([]);
  const location = useLocation();
  const member = location.state?.member || {};
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const { role } = useContext(AuthContext);
  // Free card can only be set or cleared by global admins (the server ignores anyone else's value).
  const canSetFreeCard = role === "global_admin";

  const handleFormSubmit = async (values, { resetForm }) => {
    console.log(values);
    try {
      let response;
      if (values.memberId) {
        // Update existing member
        response = await axios.put(
          `${config.apiBaseUrl}/update_member/${values.memberId}`,
          values,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${document.cookie
                .split("; ")
                .find((row) => row.startsWith("teba_auth_token="))
                ?.split("=")[1]}`,
            },
          }
        );
        if (response.status === 200) {
          toast.success("Member updated successfully", {
            onClose: () => {
              navigate("/member_list");
            }
          });
        } else {
          toast.error("Failed to update member");
        }
      } else {
        // Add new member
        response = await axios.post(
          `${config.apiBaseUrl}/add_member`,
          values,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${document.cookie
                .split("; ")
                .find((row) => row.startsWith("teba_auth_token="))
                ?.split("=")[1]}`,
            },
          }
        );

        if (response.status === 200) {
          const membershipId = response.data.membership_id;
          console.log("Membership ID:", membershipId);

          // Upload profile image
          await uploadProfileImage(membershipId, values.profileImage);

          toast.success("Member added successfully");
          resetForm();
          if (fileInputRef.current) {
            fileInputRef.current.value = ""; // Clear file input
          }
        } else {
          toast.error("Failed to add member");
        }
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to save member");
    }
  };

  const uploadProfileImage = async (membershipId, file) => {
    if (!file) {
      return false;
    }

    const fileName = await handleFileUpload(membershipId, file);
    if (fileName) {
      try {
        await axios.put(
          `${config.apiBaseUrl}/set_profile_image/${membershipId}`,
          { profileImage: fileName },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${document.cookie
                .split("; ")
                .find((row) => row.startsWith("teba_auth_token="))
                ?.split("=")[1]}`,
            },
          });
        return true;
      } catch (error) {
        toast.error("Failed to set profile image: ", error);
        return false;
      }
    } else {
      return false;
    }
  };

  const handleFileUpload = async (membershipId, file) => {
    if (!file) {
      toast.error("No file selected");
      return null;
    }

    if (!["image/jpeg", "image/jpg", "image/png"].includes(file.type)) {
      toast.error("Invalid file type. Only JPEG, JPG, and PNG are allowed.");
      return null;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size exceeds 5MB");
      return null;
    }

    try {
      return await uploadProfileImageToFirebase(membershipId, file);
    } catch (error) {
      const defaultMessage = isFirebaseStorageEnabled
        ? "Failed to upload file to Firebase Storage"
        : "Firebase Storage is not configured";
      toast.error(error?.message || defaultMessage);
      return null;
    }
  };

  useEffect(() => {
    const Countries = [
      { id: 1, name: "Sri Lanka" },
      { id: 2, name: "Maldives" },
    ];

    const Genders = [
      { id: 1, name: "male" },
      { id: 2, name: "female" },
      { id: 3, name: "other" },
    ];

    const fetchEnrollments = async () => {
      try {
        const response = await axios.get(`${config.apiBaseUrl}/get_disciplines`);
        console.log(response.data);
        setEnrollments(response.data.disciplines);
      } catch (error) {
        console.error("Failed to fetch enrollments:", error);
        toast.error("Failed to fetch enrollments");
      }
    };

    const fetchSubscriptions = async () => {
      try {
        const response = await axios.get(`${config.apiBaseUrl}/get_subscriptions`);
        console.log(response.data);
        setSubscriptions(response.data.subscriptions);
      } catch (error) {
        console.error("Failed to fetch subscriptions:", error);
        toast.error("Failed to fetch subscriptions");
      }
    };

    const fetchBranches = async () => {
      try {
        const response = await axios.get(`${config.apiBaseUrl}/get_branches`);
        console.log(response.data);
        setBranches(response.data.branches);
      } catch (error) {
        console.error("Failed to fetch branches:", error);
        toast.error("Failed to fetch branches");
      }
    };

    setCountries(Countries);
    setGenders(Genders);
    fetchEnrollments();
    fetchSubscriptions();
    fetchBranches();
  }, []);

  const initialValues = {
    memberId: member.member_id || "",
    membershipId: member.membership_id || "",
    firstName: member.first_name || "",
    lastName: member.last_name || "",
    nic: member.nic || "",
    disciplineIdOne: member.discipline_id_one || "",
    disciplineIdTwo: member.discipline_id_two || "",
    disciplineIdThree: member.discipline_id_three || "",
    subscription: member.subscription_id || "",
    rfid: member.rfid || "",
    homeAddress: member.home_address || "",
    city: member.city || "",
    country: member.country || "",
    branch: member.branch_id || "",
    studentDob: member.student_dob || "",
    occupation: member.occupation || "",
    gender: member.gender || "",
    residencePhone: member.residence_phone || "",
    mobilePhone: member.mobile_phone || "",
    email: member.email || "",
    whatsappNumber: member.whatsapp_number || "",
    emergencyContactPersonName: member.emergency_contact_person_name || "",
    emergencyContactNumber: member.emergency_contact_number || "",
    family: member.family_id || "",
    profileImage: member.profile_image || "",
    ...(canSetFreeCard ? { isFreeCard: Boolean(Number(member.is_free_card)) } : {}),
  };

  return (
    <Box m={{ xs: "12px", sm: "16px", md: "20px" }}>
      <Header title="ADD | MODIFY USER" subtitle="Create or Modify a User Profile" />
      <ToastContainer />
      <Formik
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        validationSchema={checkoutSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          setFieldValue,
          handleSubmit,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap={{ xs: 2, sm: 2.5, md: 3 }}
              gridTemplateColumns={{ xs: "repeat(1, minmax(0, 1fr))", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" }}
            >
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Membership ID"
                onBlur={handleBlur}
                onChange={(e) => setFieldValue("membershipId", e.target.value)}
                value={values.membershipId}
                name="membershipId"
                error={!!touched.membershipId && !!errors.membershipId}
                helperText={touched.membershipId && errors.membershipId}
                sx={{ gridColumn: "span 1" }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="First Name"
                onBlur={handleBlur}
                onChange={(e) => setFieldValue("firstName", e.target.value)}
                value={values.firstName}
                name="firstName"
                error={!!touched.firstName && !!errors.firstName}
                helperText={touched.firstName && errors.firstName}
                sx={{ gridColumn: { xs: "span 1", lg: "span 2" } }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Last Name"
                onBlur={handleBlur}
                onChange={(e) => setFieldValue("lastName", e.target.value)}
                value={values.lastName}
                name="lastName"
                error={!!touched.lastName && !!errors.lastName}
                helperText={touched.lastName && errors.lastName}
                sx={{ gridColumn: "span 1" }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="NIC"
                onBlur={handleBlur}
                onChange={(e) => setFieldValue("nic", e.target.value)}
                value={values.nic}
                name="nic"
                error={!!touched.nic && !!errors.nic}
                helperText={touched.nic && errors.nic}
                sx={{ gridColumn: "span 1" }}
              />
              <FormControl fullWidth variant="filled" error={!!touched.disciplineIdOne && !!errors.disciplineIdOne} sx={{ gridColumn: "span 1" }}>
                <InputLabel id="discipline-one-label">Discipline 1 (Primary) *</InputLabel>
                <Select
                  labelId="discipline-one-label"
                  value={values.disciplineIdOne}
                  onChange={(e) => setFieldValue("disciplineIdOne", e.target.value)}
                >
                  {enrollments.map((enrollment) => (
                    <MenuItem key={enrollment.discipline_id} value={enrollment.discipline_id}>
                      {enrollment.discipline_name}
                    </MenuItem>
                  ))}
                </Select>
                {touched.disciplineIdOne && errors.disciplineIdOne && (
                  <FormHelperText>{errors.disciplineIdOne}</FormHelperText>
                )}
              </FormControl>
              <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 1" }}>
                <InputLabel id="discipline-two-label">Discipline 2 (Optional)</InputLabel>
                <Select
                  labelId="discipline-two-label"
                  value={values.disciplineIdTwo}
                  onChange={(e) => setFieldValue("disciplineIdTwo", e.target.value)}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {enrollments.map((enrollment) => (
                    <MenuItem key={enrollment.discipline_id} value={enrollment.discipline_id}>
                      {enrollment.discipline_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 1" }}>
                <InputLabel id="discipline-three-label">Discipline 3 (Optional)</InputLabel>
                <Select
                  labelId="discipline-three-label"
                  value={values.disciplineIdThree}
                  onChange={(e) => setFieldValue("disciplineIdThree", e.target.value)}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {enrollments.map((enrollment) => (
                    <MenuItem key={enrollment.discipline_id} value={enrollment.discipline_id}>
                      {enrollment.discipline_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth variant="filled" error={!!touched.subscription && !!errors.subscription} sx={{ gridColumn: "span 1" }}>
                <InputLabel id="subscription-label">Subscription</InputLabel>
                <Select
                  labelId="subscription-label"
                  value={values.subscription}
                  onChange={(e) => setFieldValue("subscription", e.target.value)}
                >
                  {subscriptions.map((subscription) => (
                    <MenuItem key={subscription.subscription_id} value={subscription.subscription_id}>
                      {subscription.subscription_name}
                    </MenuItem>
                  ))}
                </Select>
                {touched.subscription && errors.subscription && (
                  <FormHelperText>{errors.subscription}</FormHelperText>
                )}
              </FormControl>
              {canSetFreeCard && (
                <FormControl sx={{ gridColumn: "span 1", justifyContent: "center" }}>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={Boolean(values.isFreeCard)}
                        onChange={(e) => setFieldValue("isFreeCard", e.target.checked)}
                        color="secondary"
                      />
                    )}
                    label="Free card"
                  />
                  <FormHelperText sx={{ mt: 0 }}>
                    Never payment pending. The family pays nothing only when every member has a free card.
                  </FormHelperText>
                </FormControl>
              )}
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="RFID"
                onBlur={handleBlur}
                onChange={(e) => setFieldValue("rfid", e.target.value)}
                value={values.rfid}
                name="rfid"
                error={!!touched.rfid && !!errors.rfid}
                helperText={touched.rfid && errors.rfid}
                sx={{ gridColumn: "span 1" }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Home Address"
                onBlur={handleBlur}
                onChange={(e) => setFieldValue("homeAddress", e.target.value)}
                value={values.homeAddress}
                name="homeAddress"
                error={!!touched.homeAddress && !!errors.homeAddress}
                helperText={touched.homeAddress && errors.homeAddress}
                sx={{ gridColumn: { xs: "span 1", lg: "span 2" } }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="City"
                onBlur={handleBlur}
                onChange={(e) => setFieldValue("city", e.target.value)}
                value={values.city}
                name="city"
                error={!!touched.city && !!errors.city}
                helperText={touched.city && errors.city}
                sx={{ gridColumn: "span 1" }}
              />
              <FormControl fullWidth variant="filled" error={!!touched.country && !!errors.country} sx={{ gridColumn: "span 1" }}>
                <InputLabel id="country-label">Country</InputLabel>
                <Select
                  labelId="country-label"
                  value={values.country}
                  onChange={(e) => setFieldValue("country", e.target.value)}
                >
                  {countries.map((country) => (
                    <MenuItem key={country.id} value={country.name}>
                      {country.name}
                    </MenuItem>
                  ))}
                </Select>
                {touched.country && errors.country && (
                  <FormHelperText>{errors.country}</FormHelperText>
                )}
              </FormControl>
              <FormControl fullWidth variant="filled" error={!!touched.branch && !!errors.branch} sx={{ gridColumn: "span 1" }}>
                <InputLabel id="branch-label">Branch</InputLabel>
                <Select
                  labelId="branch-label"
                  value={values.branch}
                  onChange={(e) => setFieldValue("branch", e.target.value)}
                >
                  {branches.map((branch) => (
                    <MenuItem key={branch.branch_id} value={branch.branch_id}>
                      {branch.branch_name}
                    </MenuItem>
                  ))}
                </Select>
                {touched.branch && errors.branch && (
                  <FormHelperText>{errors.branch}</FormHelperText>
                )}
              </FormControl>
              <TextField
                fullWidth
                variant="filled"
                label="Date of Birth"
                type="date"
                onBlur={handleBlur}
                onChange={(e) => setFieldValue("studentDob", e.target.value)}
                value={values.studentDob}
                name="studentDob"
                error={!!touched.studentDob && !!errors.studentDob}
                helperText={touched.studentDob && errors.studentDob}
                sx={{ gridColumn: "span 1" }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Occupation"
                onBlur={handleBlur}
                onChange={(e) => setFieldValue("occupation", e.target.value)}
                value={values.occupation}
                name="occupation"
                error={!!touched.occupation && !!errors.occupation}
                helperText={touched.occupation && errors.occupation}
                sx={{ gridColumn: "span 1" }}
              />
              <FormControl fullWidth variant="filled" error={!!touched.gender && !!errors.gender} sx={{ gridColumn: "span 1" }}>
                <InputLabel id="gender-label">Gender</InputLabel>
                <Select
                  labelId="gender-label"
                  value={values.gender}
                  onChange={(e) => setFieldValue("gender", e.target.value)}
                >
                  {genders.map((gender) => (
                    <MenuItem key={gender.id} value={gender.name}>
                      {gender.name}
                    </MenuItem>
                  ))}
                </Select>
                {touched.gender && errors.gender && (
                  <FormHelperText>{errors.gender}</FormHelperText>
                )}
              </FormControl>
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Residence Phone"
                onBlur={handleBlur}
                onChange={(e) => setFieldValue("residencePhone", e.target.value)}
                value={values.residencePhone}
                name="residencePhone"
                error={!!touched.residencePhone && !!errors.residencePhone}
                helperText={touched.residencePhone && errors.residencePhone}
                sx={{ gridColumn: "span 1" }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Mobile Phone"
                onBlur={handleBlur}
                onChange={(e) => setFieldValue("mobilePhone", e.target.value)}
                value={values.mobilePhone}
                name="mobilePhone"
                error={!!touched.mobilePhone && !!errors.mobilePhone}
                helperText={touched.mobilePhone && errors.mobilePhone}
                sx={{ gridColumn: "span 1" }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Email"
                onBlur={handleBlur}
                onChange={(e) => setFieldValue("email", e.target.value)}
                value={values.email}
                name="email"
                error={!!touched.email && !!errors.email}
                helperText={touched.email && errors.email}
                sx={{ gridColumn: "span 1" }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Whatsapp Number"
                onBlur={handleBlur}
                onChange={(e) => setFieldValue("whatsappNumber", e.target.value)}
                value={values.whatsappNumber}
                name="whatsappNumber"
                error={!!touched.whatsappNumber && !!errors.whatsappNumber}
                helperText={touched.whatsappNumber && errors.whatsappNumber}
                sx={{ gridColumn: "span 1" }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Emergency Contact Person Name"
                onBlur={handleBlur}
                onChange={(e) => setFieldValue("emergencyContactPersonName", e.target.value)}
                value={values.emergencyContactPersonName}
                name="emergencyContactPersonName"
                error={!!touched.emergencyContactPersonName && !!errors.emergencyContactPersonName}
                helperText={touched.emergencyContactPersonName && errors.emergencyContactPersonName}
                sx={{ gridColumn: "span 1" }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Emergency Contact Number"
                onBlur={handleBlur}
                onChange={(e) => setFieldValue("emergencyContactNumber", e.target.value)}
                value={values.emergencyContactNumber}
                name="emergencyContactNumber"
                error={!!touched.emergencyContactNumber && !!errors.emergencyContactNumber}
                helperText={touched.emergencyContactNumber && errors.emergencyContactNumber}
                sx={{ gridColumn: "span 1" }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Family"
                onBlur={handleBlur}
                onChange={(e) => setFieldValue("family", e.target.value)}
                value={values.family}
                name="family"
                error={!!touched.family && !!errors.family}
                helperText={touched.family && errors.family}
                sx={{ gridColumn: "span 1" }}
                InputProps={{
                  readOnly: true,
                }}
              />
              {/* {values.membershipId && (
                <Box sx={{ gridColumn: "span 1" }}>
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png"
                    onChange={async (event) => {
                      const file = event.target.files[0];
                      const fileName = await handleFileUpload(values.membershipId, file);
                      if (fileName) {
                        setFieldValue("profileImage", fileName);
                      }
                    }}
                  />
                </Box>
              )} */}
              {!values.memberId && (
                <Box sx={{ gridColumn: "span 1" }}>
                  <input
                    type="file"
                    accept="image/jpeg,image/jpg,image/png"
                    onChange={(event) => {
                      const file = event.target.files[0];
                      setFieldValue("profileImage", file);
                    }}
                    ref={fileInputRef}
                  />
                </Box>
              )}
            </Box>
            <Box display="flex" justifyContent="end" mt={{ xs: 2, sm: 2.5 }}>
              <Button type="submit" color="secondary" variant="contained">
                Save
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

const phoneRegExp =
  /^((\+[1-9]{1,4}[ -]?)|(\([0-9]{2,3}\)[ -]?)|([0-9]{2,4})[ -]?)*?[0-9]{3,4}[ -]?[0-9]{3,4}$/;

const checkoutSchema = yup.object().shape({
  membershipId: yup.string().required("Membership ID is required"),
  firstName: yup.string().nullable(),
  lastName: yup.string().nullable(),
  disciplineIdOne: yup.string().required("Primary discipline is required"),
  disciplineIdTwo: yup.string().nullable(),
  disciplineIdThree: yup.string().nullable(),
  subscription: yup.string().required("required"),
  homeAddress: yup.string().nullable(),
  city: yup.string().nullable(),
  country: yup.string().required("required"),
  branch: yup.string().required("required"),
  studentDob: yup.date().nullable(),
  gender: yup.string().nullable(),
  residencePhone: yup
    .string()
    .matches(phoneRegExp, "Phone number is not valid")
    .nullable(),
  mobilePhone: yup
    .string()
    .matches(phoneRegExp, "Phone number is not valid")
    .nullable(),
  email: yup.string().email("invalid email").nullable(),
  whatsappNumber: yup
    .string()
    .matches(phoneRegExp, "Phone number is not valid")
    .nullable(),
  emergencyContactPersonName: yup.string().nullable(),
  emergencyContactNumber: yup
    .string()
    .matches(phoneRegExp, "Phone number is not valid")
    .nullable(),
});



export default ManageMember;